import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Box,
  Typography,
  Slide,
  Link,
  Grid
} from "@material-ui/core";
import { connect } from "react-redux";
import AddIcon from "@material-ui/icons/Add";
import BlackButton from "../BlackButton";
import AddWebhook from "./AddWebhook";
import clsx from "clsx";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import API from "../../../utils/axios";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import { setEventTypes, setWebhooks } from "../../../store/actions/account";
import { useSnackbar } from "notistack";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import Skeleton from "@material-ui/lab/Skeleton";

const useStyles = makeStyles(theme => ({
  animationWrapper: {
    backgroundColor: "#fff"
  },
  formWrapper: {
    overflow: "hidden",
    boxSizing: "border-box",
    transition: theme.transitions.create(["padding"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },

  driverRow: {
    width: "100%",
    margin: 0,
    borderBottom: "1px solid #eee !important",
    backgroundColor: "#f9f9f9",

    "& .MuiGrid-item": {
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.25)
    },

    "& .edit-remove-btns": {
      opacity: 0,
      transition: "opacity .3s",

      "& button": {
        marginLeft: theme.spacing(1.5)
      }
    },

    "& > :last-of-type p": {
      transition: "opacity .3s"
    },

    "&:hover": {
      "& > :last-of-type p": {
        opacity: 0,
        userSelect: "none"
      },

      "& .edit-remove-btns": {
        opacity: 1
      }
    }
  },
  addButton: {
    backgroundColor: theme.palette.text.primary
  },
  borderTop: {
    borderTop: "1px solid #eee !important"
  },
  title: {
    lineHeight: "1.1875rem",
    color: "#202124",
    marginBottom: theme.spacing(2),
    fontWeight: 500
  },
  notFoundText: {
    color: "#808080",
    lineHeight: "1.1875rem"
  },
  addIcon: {
    color: "#fff",
    margin: theme.spacing(-0.5, 1, -0.5, 0)
  }
}));

function WebhooksTab({
  drawerExpanded,
  setLoading,
  setWebhooks,
  webhooks,
  organisationLoaded,
  eventTypes,
  setEventTypes,
  plan
}) {
  const classes = useStyles();

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    id: null
  });
  const { enqueueSnackbar } = useSnackbar();
  const [webhooksLoaded, setWebhooksLoaded] = useState(false);

  /**
   * Deletes webhook by given id
   * @param  {string} id webhook id
   * @return {null}
   */
  const deleteWebhook = id => {
    setLoading(true);
    API.delete(`v1/webhook/${id}`)
      .then(res => {
        enqueueSnackbar("Webhook successfully deleted");
        setWebhooks(webhooks.filter(webhook => webhook.id !== id));
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!webhooks.length && plan.ft_api_integration) {
      const requests = [
        API.get("v1/webhook/list"),
        API.get("v1/webhook/event-types")
      ];
      Promise.all(requests)
        .then(res => {
          setWebhooks(res[0].data);
          setEventTypes(res[1].data);
          setWebhooksLoaded(true);
        })
        .catch(err => console.log(err));
    } else {
      if (organisationLoaded && plan.ft_api_integration !== undefined) {
        setWebhooksLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.ft_api_integration]);

  const [currentWebhook, setCurrentWebhook] = useState(false);

  const [addWebhookVisible, setAddWebhookVisible] = useState(false);

  return (
    <Box
      pt={4}
      mr={-2}
      display="flex"
      flexDirection="column"
      height="100%"
      style={{ boxSizing: "border-box" }}
    >
      {!webhooksLoaded ? (
        <>
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} />
        </>
      ) : plan && plan.ft_api_integration ? (
        <>
          <Typography variant="body1" className={classes.title}>
            Endpoints
          </Typography>

          {webhooks.length ? (
            webhooks.map((webhook, index) => (
              <Box
                component={Grid}
                key={webhook.id}
                container
                spacing={4}
                height={50}
                alignItems="center"
                position="relative"
                className={clsx(classes.driverRow, {
                  [classes.borderTop]: index === 0
                })}
              >
                <Grid item xs={10} style={{ paddingRight: 16 }}>
                  <Typography variant="body2" noWrap>
                    {webhook.url}
                  </Typography>
                </Grid>

                {/* Edit/remove buttons */}
                <Box
                  alignSelf="center"
                  display="flex"
                  alignItems="center"
                  className="edit-remove-btns"
                  position="absolute"
                  my="auto"
                  right={12}
                >
                  <Link
                    component="button"
                    onClick={() => {
                      setCurrentWebhook(webhook);
                      setAddWebhookVisible(true);
                    }}
                  >
                    <OptimizedTooltip
                      placement="bottom"
                      title={`Edit webhook details`}
                    >
                      <EditIcon />
                    </OptimizedTooltip>
                  </Link>

                  <Link
                    component="button"
                    onClick={() => {
                      setConfirmDialogState({ open: true, id: webhook.id });
                    }}
                  >
                    <DeleteIcon />
                  </Link>
                </Box>
              </Box>
            ))
          ) : (
            <Box>
              <Typography
                variant="body1"
                color="textSecondary"
                className={classes.notFoundText}
              >
                You have not defined any webhooks yet
              </Typography>
            </Box>
          )}

          <Box
            mt={4}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <BlackButton
              content={
                <>
                  <AddIcon className={classes.addIcon} /> Add endpoint
                </>
              }
              handleClick={() => setAddWebhookVisible(true)}
              disabled={webhooks.length >= 10}
              className={classes.addButton}
              data-cy="addWebhook"
              style={{
                paddingLeft: "0.75rem",
                paddingRight: "1rem"
              }}
            />

            <Typography variant="body2" color={"textSecondary"}>
              {!webhooks.length ? "0" : webhooks.length}/10
            </Typography>
          </Box>

          <Slide
            in={addWebhookVisible}
            direction="right"
            unmountOnExit
            mountOnEnter
          >
            <Box
              position="absolute"
              left="0"
              right="0"
              top="0"
              minHeight={"100vh"}
              zIndex={50}
              className={classes.animationWrapper}
            >
              <Box
                className={classes.formWrapper}
                style={{ paddingLeft: drawerExpanded ? 280 : 56 }}
                display="flex"
                flexDirection="column"
                height="100%"
              >
                <AddWebhook
                  setAddWebhookVisible={setAddWebhookVisible}
                  currentWebhook={currentWebhook}
                  eventTypes={eventTypes}
                  setCurrentWebhook={setCurrentWebhook}
                  setWebhooks={setWebhooks}
                  webhooks={webhooks}
                  setLoading={setLoading}
                  handleClose={() => {
                    setAddWebhookVisible(false);
                    setCurrentWebhook(null);
                  }}
                />
              </Box>
            </Box>
          </Slide>

          <ConfirmDialog
            open={confirmDialogState.open}
            handleClose={() => setConfirmDialogState({ open: false })}
            action={() => deleteWebhook(confirmDialogState.id)}
            title={"Delete webhook?"}
            body={
              "Are you sure you want to delete this webhook? Once deleted, it can not be undone."
            }
          />
        </>
      ) : (
        <Typography
          variant="body1"
          color="textSecondary"
          className={classes.notFoundText}
        >
          Please upgrade your plan
        </Typography>
      )}
    </Box>
  );
}

const mapStateToProps = state => ({
  webhooks: state.account.webhooks,
  eventTypes: state.account.webhookEventTypes,
  organisationLoaded: !!state.auth.userData.organisationSite,
  plan:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation.plan) ||
    {}
});

export default connect(mapStateToProps, { setEventTypes, setWebhooks })(
  WebhooksTab
);
